<template>
  <div v-if="NOTARIA" class="w-100">
    <v-row class="mb-5" v-if="!props.Home && orderedBlog.length > 0">
      <v-col cols="12" class="d-flex justify-end pr-5">
        <v-btn variant="text" :style="{ color: buttonColor }" density="compact" @click="toggleSort">
          <v-icon start :color="buttonColor">{{ sortDesc ? 'mdi-sort-clock-descending-outline' : 'mdi-sort-clock-ascending-outline' }}</v-icon>
          {{ $t('ordenar') }}: {{ sortDesc ? $t('masRecientesPrimero') : $t('masAntiguosPrimero') }}
        </v-btn>
      </v-col>
    </v-row>
    
    <!-- Start Search Area -->
    <v-row v-if="!props.Home && NOTARIA && !store.loading && store.NOTARIA?.Theme?.Blog?.MostrarBuscador !== false" class="mb-4 justify-center">
      <v-col cols="12" md="8" lg="6">
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          label="Buscar artículo..."
          variant="outlined"
          density="comfortable"
          hide-details
          clearable
          class="search-bar-notin"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row v-if="!props.Home && searchQuery.trim() !== '' && store.NOTARIA?.Personalizar?.ChatbotActivo && !store.chatbotOverQuota" class="mb-5 justify-center">
      <v-col cols="12" class="text-center">
        <v-btn
          :color="store.NOTARIA?.Personalizar?.ChatbotColor || 'primary'"
          variant="outlined"
          prepend-icon="mdi-robot-outline"
          @click="askAi"
          class="mt-2 text-none"
        >
          Preguntar a la Inteligencia Artificial sobre '{{ searchQuery }}'
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="!props.Home && searchQuery.trim() !== '' && orderedBlog.length === 0" class="mb-5 justify-center">
      <v-col cols="12" class="text-center text-muted">
        <p>No se han encontrado artículos que coincidan con la búsqueda.</p>
      </v-col>
    </v-row>
    <!-- End Search Area -->

    <!-- Start Blog Collapsible Banner -->
    <v-row v-if="!props.Home && store.NOTARIA?.Theme?.Blog?.Banner?.Mostrar" class="mb-2">
      <v-col cols="12">
        <BlogBannerCollapsible :banner="store.NOTARIA?.Theme?.Blog?.Banner" />
      </v-col>
    </v-row>
    <!-- End Blog Collapsible Banner -->

    <!-- Skeleton Loader Area -->
    <v-row v-if="store.loading">
      <v-col cols="12" sm="6" :md="comNumColumnas" v-for="n in 6" :key="'skeleton-' + n">
        <v-card class="blog blog-style-notin border-0 bg-transparent elevation-0">
          <v-skeleton-loader
            elevation="0"
            type="image, article"
            :color="styles.ThemeGeneral?.TextoCss?.Color ? styles.ThemeGeneral.TextoCss.Color + '10' : 'rgba(0,0,0,0.05)'"
            class="rounded-lg"
          ></v-skeleton-loader>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="!store.loading">
      <!-- Start Blog Area  -->
      <v-col cols="12" sm="6" :md="comNumColumnas" v-for="(item, i) in paginatedBlog" :key="i">
        <div :class="'blog blog-style-notin card-' + (type || 'blog') + '-notin' + (props.Home ? '' : ' clickable-card')" @click="$router.push('/blog/' + item.Slug)">
          <div class="thumbnail">
            <NuxtLink :to="'/blog/' + item.Slug">
              <img class="w-100" :src="imagenIDtoSRC(item.Imagen)" :alt="'Imagen de la entrada: ' + item.Titulo" />
            </NuxtLink>
          </div>
          <div class="content">
            <h4 :class="props.Home ? 'heading-title' : 'title-card-blog'">
              <NuxtLink :to="'/blog/' + item.Slug">{{ item.Titulo }}</NuxtLink>
            </h4>
            <p class="blogtype blog-meta-hover mt-2">
               <span v-if="item.Fecha"><v-icon size="small" class="mr-1 mb-2">mdi-calendar-blank</v-icon>{{ formatDateNotin(item.Fecha) }}</span>
               <span v-if="item.Contenido" class="ml-4"><v-icon size="small" class="mr-1 mb-2">mdi-clock-outline</v-icon>{{ getReadTime(item.Contenido) }} min lectura</span>
            </p>
            <div class="blog-categories-hover" v-if="item.Categorias && item.Categorias.length > 0">
               <v-chip size="x-small" variant="outlined" class="mr-1 mb-1 text-uppercase" style="border-color: currentColor;" v-for="cat in item.Categorias" :key="cat">
                  {{ cat }}
               </v-chip>
            </div>
            <p class="blogtype blog-excerpt-hover mt-3" v-if="item.Extracto">{{ item.Extracto }}</p>
          </div>
        </div>
    </v-col>
    <!-- End Blog Area  -->
    </v-row>
    <v-row v-if="orderedBlog.length > visibleCount" class="mt-5">
      <v-col class="text-center">
        <v-btn :style="{ color: buttonColor, borderColor: buttonColor }" variant="outlined" @click="visibleCount += 6">{{ $t('verMas') }}</v-btn>
      </v-col>
    </v-row>
  </div>
</template>
<script setup lang="ts">
import { useStore } from '../../store/index'
const store = useStore()
const { NOTARIA } = storeToRefs(store)
import { useStyles } from '../../store/styles'
const styles = useStyles()
import { useUtils } from '#imports';
import BlogBannerCollapsible from './BlogBannerCollapsible.vue';
const { imagenIDtoSRC, $t } = useUtils()
const props = defineProps({
  Home: Boolean,
  type: String,
  customBlogs: Array
})

const sortDesc = ref(true);
const toggleSort = () => {
  sortDesc.value = !sortDesc.value;
};

const searchQuery = ref('');

const askAi = () => {
    store.pendingChatQuery = searchQuery.value;
    store.isChatbotOpen = true;
};

const parseDateNotin = (dateStr: string) => {
  if (!dateStr) return 0;

  // Try custom parsing first for DD/MM/YYYY format which is ambiguous for new Date()
  if (dateStr.includes('/')) {
     const parts = dateStr.split(/[\s,:/]+/);
     if (parts.length >= 3) {
       // If the first part is 4 digits, it's likely YYYY/MM/DD, let new Date() handle it
       if (parts[0].length !== 4) {
         const day = parseInt(parts[0], 10);
         const month = parseInt(parts[1], 10) - 1;
         const year = parseInt(parts[2], 10);
         const hh = parts[3] ? parseInt(parts[3], 10) : 0;
         const min = parts[4] ? parseInt(parts[4], 10) : 0;
         const sec = parts[5] ? parseInt(parts[5], 10) : 0;
         if(year > 1000 && month >= 0 && month <= 11 && day >= 1 && day <= 31) {
            return new Date(year, month, day, hh, min, sec).getTime();
         }
       }
     }
  }

  const d = new Date(dateStr);
  const t = d.getTime();
  if (!isNaN(t)) return t;

  return 0;
};

const formatDateNotin = (dateStr: string) => {
  const ts = parseDateNotin(dateStr);
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
};

const orderedBlog = computed(() => {
  const notaria: any = store.NOTARIA;
  if (!notaria || !notaria.Blog) return [];
  let blogArray = props.customBlogs ? [...props.customBlogs] : [...notaria.Blog];
  
  // Filtrar artículos que no deben mostrarse
  blogArray = blogArray.filter((item: any) => {
    const isPublic = item.Publicado;
    if (!isPublic) return false;
    
    if (props.Home) {
      // En la Home, forzar que tenga MostrarHome activado y que tenga imagen
      return item.MostrarHome && item.Imagen;
    }
    return true;
  });

  if (searchQuery.value.trim() !== '') {
    const q = searchQuery.value.toLowerCase();
    blogArray = blogArray.filter((item: any) => 
      (item.Titulo && item.Titulo.toLowerCase().includes(q)) || 
      (item.Extracto && item.Extracto.toLowerCase().includes(q)) ||
      (item.Categorias && item.Categorias.some((c: string) => c.toLowerCase().includes(q)))
    );
  }

  return blogArray.sort((a: any, b: any) => {
    const dateA = parseDateNotin(a.Fecha);
    const dateB = parseDateNotin(b.Fecha);
    return sortDesc.value ? dateB - dateA : dateA - dateB;
  });
});

const getReadTime = (htmlContent: string) => {
  if (!htmlContent) return 1;
  const text = htmlContent.replace(/<[^>]*>?/gm, '');
  const wpm = 200;
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / wpm));
};

const visibleCount = ref(6);
const paginatedBlog = computed(() => {
  return orderedBlog.value.slice(0, visibleCount.value);
});

const comNumColumnas = computed(()=> {
    const notaria: any = store.NOTARIA;
    if (!notaria || !notaria.Theme) return 12;
    
    // Si estamos en la Home, intentar usar la configuración de columnas de la Home
    // si no, caer en la del Blog general
    const numCols = props.Home && notaria.Theme.Home?.Blog?.NumeroColumnas 
                    ? notaria.Theme.Home.Blog.NumeroColumnas 
                    : notaria.Theme.Blog.NumeroColumnas;
                    
    if (numCols == 1) return 12;
    if (numCols == 2) return 6;
    if (numCols == 3) return 4;
    if (numCols == 4) return 3;
    return 12;
  })

const cardStyles = computed(() => {
  const notaria: any = store.NOTARIA;
  if (!notaria || !notaria.Theme) return { tituloColor: '' };
  
  const themeNode = props.Home ? notaria.Theme.Home?.Blog : notaria.Theme.Blog;
  if (!themeNode || !themeNode.Tarjeta) return { tituloColor: '' };

  const t = themeNode.Tarjeta;
  
  let fondoStr = "transparent";
  if (t.FondoCSS?.BackgroundColor && t.FondoCSS?.BackgroundColorGradient) {
    fondoStr = "linear-gradient(to bottom," + t.FondoCSS.BackgroundColor +" 0,"+ t.FondoCSS.BackgroundColorGradient+" 100%)";
  } else if (t.FondoCSS?.BackgroundColor) {
    fondoStr = t.FondoCSS.BackgroundColor;
  }

  return {
    tituloColor: t.Titulo?.css?.Color || "",
    tituloShadow: t.Titulo?.css?.BackgroundColor ? "4px 2px 6px "+t.Titulo.css.BackgroundColor : "",
    tituloFont: t.Titulo?.css?.Font || "",
    tituloSize: t.Titulo?.css?.Size || "",
    tituloWeight: t.Titulo?.css?.Weight || "",
    tituloAlign: t.Titulo?.css?.Align || "",
    fondo: fondoStr
  };
});

const buttonColor = computed(() => {
  const notaria: any = store.NOTARIA;
  if (!notaria || !notaria.Theme) return '#333333';
  
  const themeNode = props.Home ? notaria.Theme.Home?.Blog : notaria.Theme.Blog;
  if (!themeNode) return '#333333';

  // Lista priorizada de candidatos para el color de los botones (buscamos contraste con fondo blanco)
  const candidates = [
    themeNode.Tarjeta?.Descripcion?.css?.Color,
    notaria.Theme.General?.TextoCss?.Color,
    themeNode.Subtitulo?.css?.Color, 
    themeNode.Titulo?.css?.BackgroundColor,
    themeNode.Tarjeta?.Titulo?.css?.Color,
    themeNode.Titulo?.css?.Color,
    notaria.Theme.Footer?.FondoCSS?.BackgroundColor
  ];

  let selectedColor = '#333333';
  for (const c of candidates) {
    if (c && typeof c === 'string') {
      const normalized = c.toLowerCase().trim();
      // Filtro inicial para descartar colores sin contraste
      const isInvalid = /^(#fff|#ffffff|#ffffffff|white|transparent|#00000000)$/.test(normalized) ||
                        /^(#f[a-f0-9]{2,}|#e[a-f0-9]{2,})$/.test(normalized) ||
                        /^(rgba?\(255,\s*255,\s*255[,)]|rgba?\(0,\s*0,\s*0,\s*0\))/.test(normalized) ||
                        normalized.includes('white');

      if (!isInvalid) {
        selectedColor = normalized;
        break;
      }
    }
  }

  // Comprobación posterior sobre el blanco (Filtro de seguridad final)
  if (!selectedColor || 
      selectedColor === '#fff' || 
      selectedColor === '#ffffff' || 
      selectedColor === '#ffffffff' || 
      selectedColor.toLowerCase().includes('white') ||
      selectedColor.toLowerCase().includes('rgba(255,255,255') ||
      selectedColor.toLowerCase() === 'transparent') {
    return '#333333';
  }

  return selectedColor;
})


</script>

<style scoped lang="scss">
.clickable-card {
  cursor: pointer;
}

.title-card-blog, .title-card-blog a, .heading-title, .heading-title a {
  color: v-bind('cardStyles.tituloColor') !important;
  text-shadow: v-bind('cardStyles.tituloShadow') !important;
  font-family: v-bind('cardStyles.tituloFont') !important;
  font-size: v-bind('cardStyles.tituloSize') !important;
  font-weight: v-bind('cardStyles.tituloWeight') !important;
  text-align: v-bind('cardStyles.tituloAlign') !important;
  text-decoration: none !important;
}

.blogtype, .blogtype span, .blogtype i, .blog-excerpt-hover, .blog-categories-hover .v-chip {
  color: v-bind('cardStyles.tituloColor') !important;
}

/* Asegurar que el hover overlay use el color del tema */
:deep(.thumbnail a::after) {
  background: v-bind('cardStyles.fondo') !important;
}

/* Compatibilidad con el efecto hover de SCSS */
.blog-style-notin:hover {
  .title-card-blog, .title-card-blog a, .heading-title, .heading-title a {
     /* Forzar que el color dinámico persista en hover si así lo quiere el tema */
     color: v-bind('cardStyles.tituloColor') !important;
  }
}
</style>